import { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { ExternalLink, Folder, Globe } from "lucide-react";
import { AdBanner } from "@/components/AdSense";

const API = `${getApiUrl()}/api`;

export default function Links() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await axios.get(`${API}/links/groups`);
        setGroups(res.data);
      } catch (err) {
        console.error("Failed to fetch links:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLinks();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-4xl font-bold text-white font-[Unbounded] mb-2">Links</h1>
        <p className="text-zinc-400 mb-8">Useful resources and tools</p>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const hasLinks = groups.some(g => g.links && g.links.length > 0);

  return (
    <div className="p-6" data-testid="links-page">
      <h1 className="text-4xl font-bold text-white font-[Unbounded] mb-2">Links</h1>
      <p className="text-zinc-400 mb-8">Useful resources and tools</p>

      <AdBanner slot="links-top" />

      {!hasLinks ? (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
          <Globe className="w-12 h-12 mb-4 opacity-40" />
          <p>No links have been added yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.filter(g => g.links && g.links.length > 0).map(group => (
            <div key={group.id} data-testid={`link-group-${group.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Folder className="w-5 h-5 text-purple-400" />
                <h2 className="text-xl font-bold text-white">{group.name}</h2>
              </div>
              {group.description && (
                <p className="text-zinc-500 text-sm mb-4 ml-7">{group.description}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                {group.links.map(link => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`link-item-${link.id}`}
                    className="group flex items-start gap-3 p-4 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-purple-500/50 hover:bg-zinc-800/80 transition-all duration-200"
                  >
                    {link.icon ? (
                      <img
                        src={link.icon}
                        alt=""
                        className="w-6 h-6 rounded mt-0.5 flex-shrink-0 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ExternalLink className="w-5 h-5 text-zinc-500 group-hover:text-purple-400 mt-0.5 flex-shrink-0 transition-colors" />
                    )}
                    <div className="min-w-0">
                      <p className="text-white font-medium group-hover:text-purple-300 transition-colors truncate">
                        {link.title}
                      </p>
                      {link.description && (
                        <p className="text-zinc-500 text-sm mt-0.5 line-clamp-2">{link.description}</p>
                      )}
                      <p className="text-zinc-600 text-xs mt-1 truncate">{new URL(link.url).hostname}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
